// Prompt-evaluation API: case CRUD + eval-batch start/status.
// All endpoints under /api/v1/owner/agents/{id}/eval, owner-or-admin gated.

import type { ApiClient } from '@/shared/api';
import type {
  EvalConfig,
  EvalConfigUpdate,
  EvalRun,
  EvalRunListResponse,
  EvalRunSummary,
  EvalTestCase,
  EvalTestCaseInput,
  EvalTestCaseList,
  StartEvalRunInput,
  SuggestPromptInput,
  SuggestPromptResult,
} from '../model/types';

const PROMPT_SUGGESTION_TIMEOUT_MS = 120_000;

export interface EvalApi {
  getConfig(agentId: string, signal?: AbortSignal): Promise<EvalConfig>;
  putConfig(agentId: string, input: EvalConfigUpdate, signal?: AbortSignal): Promise<EvalConfig>;
  listTestCases(agentId: string, signal?: AbortSignal): Promise<EvalTestCase[]>;
  createTestCase(agentId: string, input: EvalTestCaseInput, signal?: AbortSignal): Promise<EvalTestCase>;
  updateTestCase(
    agentId: string,
    testCaseId: string,
    input: EvalTestCaseInput,
    signal?: AbortSignal,
  ): Promise<EvalTestCase>;
  deleteTestCase(agentId: string, testCaseId: string, signal?: AbortSignal): Promise<void>;
  startRun(agentId: string, input: StartEvalRunInput, signal?: AbortSignal): Promise<EvalRun>;
  getRun(agentId: string, runId: string, signal?: AbortSignal): Promise<EvalRun>;
  listRuns(agentId: string, signal?: AbortSignal): Promise<EvalRunSummary[]>;
  suggest(agentId: string, input: SuggestPromptInput, signal?: AbortSignal): Promise<SuggestPromptResult>;
}

function evalRoot(agentId: string): string {
  return `/api/v1/owner/agents/${encodeURIComponent(agentId)}/eval`;
}

export function createEvalApi(client: ApiClient): EvalApi {
  return {
    async getConfig(agentId, signal) {
      return (await client.request({ path: `${evalRoot(agentId)}/config`, signal })) as EvalConfig;
    },

    async putConfig(agentId, input, signal) {
      return (await client.request({
        method: 'PUT',
        path: `${evalRoot(agentId)}/config`,
        body: input,
        signal,
      })) as EvalConfig;
    },

    async listTestCases(agentId, signal) {
      const raw = (await client.request({ path: `${evalRoot(agentId)}/test-cases`, signal })) as EvalTestCaseList;
      return raw.test_cases ?? [];
    },

    async createTestCase(agentId, input, signal) {
      return (await client.request({
        method: 'POST',
        path: `${evalRoot(agentId)}/test-cases`,
        body: input,
        signal,
      })) as EvalTestCase;
    },

    async updateTestCase(agentId, testCaseId, input, signal) {
      return (await client.request({
        method: 'PUT',
        path: `${evalRoot(agentId)}/test-cases/${encodeURIComponent(testCaseId)}`,
        body: input,
        signal,
      })) as EvalTestCase;
    },

    async deleteTestCase(agentId, testCaseId, signal) {
      await client.request({
        method: 'DELETE',
        path: `${evalRoot(agentId)}/test-cases/${encodeURIComponent(testCaseId)}`,
        signal,
      });
    },

    async startRun(agentId, input, signal) {
      return (await client.request({
        method: 'POST',
        path: `${evalRoot(agentId)}/runs`,
        body: input,
        signal,
      })) as EvalRun;
    },

    async getRun(agentId, runId, signal) {
      return (await client.request({
        path: `${evalRoot(agentId)}/runs/${encodeURIComponent(runId)}`,
        signal,
      })) as EvalRun;
    },

    async listRuns(agentId, signal) {
      const raw = (await client.request({ path: `${evalRoot(agentId)}/runs`, signal })) as EvalRunListResponse;
      return raw.runs ?? [];
    },

    async suggest(agentId, input, signal) {
      return (await client.request({
        method: 'POST',
        path: `${evalRoot(agentId)}/suggest`,
        body: input,
        timeoutMs: PROMPT_SUGGESTION_TIMEOUT_MS,
        signal,
      })) as SuggestPromptResult;
    },
  };
}
